import { parse } from "date-fns";

import { CivilDateProto } from "@noom/noom-contracts/noom_contracts/lib/time/civil_time";

import type { Sponsor } from "@/api/partner/getSponsors";
import type { EligibilityCheckFormValues } from "@/components/forms/EligibilityCheckForm";
import type { ProgramOffering } from "@/constants";
import type {
  EligibilityCandidateMatch,
  EligibilityInfo,
  EligibilityResponse,
} from "@/models";
import { EligibilityReason } from "@/models";
import type { EnrollmentInformation } from "@/models";
import { RetriageEligibility } from "@/models";
import { getEligibleProgramsFromEligibleOfferings } from "@/utils/programs";

export const getEligibilityCheckFormValues = (
  retriageEligibility: RetriageEligibility,
): EligibilityCheckFormValues | undefined => {
  if (!retriageEligibility.eligibilityProperties) {
    return undefined;
  }

  const { firstName, lastName, dateOfBirth, participantId } =
    retriageEligibility.eligibilityProperties;
  // month is [0-11]
  const { year, month, day } = dateOfBirth || {};
  return {
    dob: year && month && day ? new Date(year, month - 1, day) : null,
    employeeId: participantId,
    firstName: firstName || "",
    lastName: lastName || "",
  };
};

export const buildEligibilityInfo = (
  response: EligibilityResponse,
  enrollmentInfo: EnrollmentInformation | undefined,
): { eligibilityInfo: EligibilityInfo; eligiblePrograms: ProgramOffering[] } => {
  const {
    dateOfBirth,
    email,
    effectiveDate: effectiveDateString,
    eligibilityId,
    eligibleProducts,
    extras,
    firstName,
    lastName,
    participantId: employeeId,
    segmentId,
    segmentName,
    scaleEligible,
  } = response;

  const eligiblePrograms = getEligibleProgramsFromEligibleOfferings(
    eligibleProducts,
    enrollmentInfo,
  );

  const { address: addressProto, ...passthroughExtras } = extras ?? {};
  const eligibilityInfo: EligibilityInfo = {
    dob: parseEligibilityFileDate(dateOfBirth),
    effectiveDate: parseEligibilityFileDate(effectiveDateString),
    eligibilityId,
    eligiblePrograms,
    email,
    employeeId,
    firstName,
    lastName,
    segmentId,
    segmentName,
    scaleEligible,
    ...passthroughExtras,
  };
  if (addressProto) {
    eligibilityInfo.address = {
      country: addressProto.country || "",
      region: addressProto.region || "",
      city: addressProto.city || "",
      address1: addressProto.addressLine1 || "",
      address2: addressProto.addressLine2 || "",
      zipcode: addressProto.zipcode || "",
    };
  }
  return { eligibilityInfo, eligiblePrograms };
};

export const isMultipleMatchWithCandidates = (
  eligibilityReason: string | undefined,
  candidateMatches: EligibilityCandidateMatch[] | undefined,
): boolean =>
  eligibilityReason === EligibilityReason.MULTIPLE_MATCHES &&
  (candidateMatches?.length ?? 0) > 0;

export const findMatchingCandidate = (
  sponsor: Sponsor,
  candidates: EligibilityCandidateMatch[],
): EligibilityCandidateMatch | undefined => {
  const sponsorPartnerId = Number(sponsor.partnerId);
  return candidates.find(
    (match) => Number(match.partnerId) === sponsorPartnerId,
  );
};

export const parseEligibilityFileDate = (
  date?: CivilDateProto,
): Date | undefined =>
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-extra-non-null-assertion
  date ? new Date(date.year!!, date.month!! - 1, date.day) : undefined;
